// WP-1.7 (ST-2 "tag manager"): flat tag queries/mutations over the engine-backed API.
import {
  tagListResponseSchema,
  tagResponseSchema,
  type TagCreateRequest,
  type TagEditRequest,
  type TagResponse,
} from '@pfos/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from './api';

export const TAGS_KEY = ['tags'] as const;

export function useTags(
  filters: { includeArchived?: boolean } = {},
): UseQueryResult<TagResponse[]> {
  const qs = filters.includeArchived === true ? '?includeArchived=true' : '';
  return useQuery({
    queryKey: [...TAGS_KEY, filters],
    queryFn: async () => {
      const body = tagListResponseSchema.parse(await api.get(`/api/v1/tags${qs}`));
      return body.tags;
    },
  });
}

function useInvalidateTags(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: TAGS_KEY });
  };
}

export function useCreateTag(): UseMutationResult<TagResponse, Error, TagCreateRequest> {
  const invalidate = useInvalidateTags();
  return useMutation({
    mutationFn: async (input) => tagResponseSchema.parse(await api.post('/api/v1/tags', input)),
    onSuccess: invalidate,
  });
}

export function useEditTag(id: string): UseMutationResult<TagResponse, Error, TagEditRequest> {
  const invalidate = useInvalidateTags();
  return useMutation({
    mutationFn: async (input) =>
      tagResponseSchema.parse(await api.patch(`/api/v1/tags/${id}`, input)),
    onSuccess: invalidate,
  });
}
